import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import CustomerKPIs from '../components/Customer/CustomerKPIs';
import CustomerProductTable from '../components/Customer/CustomerProductTable';
import CustomerSalesHistory from '../components/Customer/CustomerSalesHistory';
import CustomerVarianceReport from '../components/Customer/CustomerVarianceReport';
import NotesManager from '../components/Common/NotesManager';

const { FiUsers, FiTrendingUp, FiPackage, FiBarChart, FiLayers, FiDollarSign } = FiIcons;

const AffiliationGroupDetails = () => {
  const { groupId } = useParams();
  const [activeTab, setActiveTab] = useState('members');

  const groupName = groupId.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

  // Mock data for members
  const memberData = {
    'clarusone': [
      { name: 'McKesson', sales: '$52.4M', growth: '+14.2%', products: 42, share: '42%' },
      { name: 'Walmart', sales: '$45.8M', growth: '+10.5%', products: 38, share: '37%' },
      { name: 'Optum', sales: '$26.3M', growth: '+11.8%', products: 35, share: '21%' }
    ],
    'redoak': [
      { name: 'CVS Health', sales: '$58.2M', growth: '+9.4%', products: 36, share: '59%' },
      { name: 'Cardinal Health', sales: '$40.0M', growth: '+7.6%', products: 34, share: '41%' }
    ],
    'wbad': [
      { name: 'Walgreens', sales: '$48.6M', growth: '+16.2%', products: 40, share: '57%' },
      { name: 'AmerisourceBergen', sales: '$37.0M', growth: '+13.8%', products: 38, share: '43%' }
    ]
  };

  const members = memberData[groupId] || [];

  const tabs = [
    { id: 'members', name: 'Group Members', icon: FiUsers },
    { id: 'overview', name: 'Aggregated Overview', icon: FiTrendingUp },
    { id: 'products', name: 'Aggregated Portfolio', icon: FiPackage },
    { id: 'variance', name: 'Group Variance', icon: FiBarChart }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'members':
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden"
          >
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Member Breakdown</h3>
              <p className="text-gray-600 mt-1">Individual performance of entities within {groupName}</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left py-4 px-6 font-semibold text-gray-900">Member Name</th>
                    <th className="text-right py-4 px-6 font-semibold text-gray-900">Total Sales</th>
                    <th className="text-right py-4 px-6 font-semibold text-gray-900">Growth</th>
                    <th className="text-center py-4 px-6 font-semibold text-gray-900">Active Products</th>
                    <th className="text-right py-4 px-6 font-semibold text-gray-900">Group Share</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((member, index) => (
                    <tr key={index} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                      <td className="py-4 px-6 font-medium text-gray-900">{member.name}</td>
                      <td className="py-4 px-6 text-right font-medium text-gray-900">{member.sales}</td>
                      <td className="py-4 px-6 text-right text-green-600 font-medium">{member.growth}</td>
                      <td className="py-4 px-6 text-center text-gray-700">{member.products}</td>
                      <td className="py-4 px-6 text-right text-blue-600 font-medium">{member.share}</td>
                    </tr>
                  ))}
                  {/* Totals Row */}
                  <tr className="bg-blue-50 font-bold border-t-2 border-blue-100">
                    <td className="py-4 px-6 text-blue-900">TOTAL {groupName}</td>
                    <td className="py-4 px-6 text-right text-blue-900">
                      ${members.reduce((acc, m) => acc + parseFloat(m.sales.slice(1)), 0).toFixed(1)}M
                    </td>
                    <td className="py-4 px-6 text-right text-green-700">+12.4%</td>
                    <td className="py-4 px-6 text-center text-blue-900">
                      {members.reduce((acc, m) => acc + m.products, 0)}
                    </td>
                    <td className="py-4 px-6 text-right text-blue-900">100%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </motion.div>
        );
      case 'overview':
        return <CustomerKPIs customerId={groupId} />;
      case 'products':
        return <CustomerProductTable customerId={groupId} />;
      case 'variance':
        return <CustomerVarianceReport customerId={groupId} />;
      default:
        return <CustomerKPIs customerId={groupId} />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6"
      >
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-indigo-700 rounded-xl flex items-center justify-center shrink-0">
              <SafeIcon icon={FiLayers} className="text-white text-xl" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{groupName}</h1>
              <p className="text-gray-600">Affiliation Group Intelligence & Aggregated Analytics</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
             <div className="hidden md:flex flex-col items-end px-4 border-r border-gray-100">
                <span className="text-xs text-gray-500 uppercase font-bold">Total Group Sales</span>
                <span className="text-lg font-bold text-blue-600">$124.5M</span>
             </div>
             <NotesManager entityId={groupId} entityType="affiliation-group" />
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <SafeIcon icon={tab.icon} />
                {tab.name}
              </button>
            ))}
          </nav>
        </div>
      </motion.div>

      {/* Tab Content */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3 }}
      >
        {renderTabContent()}
      </motion.div>
    </div>
  );
};

export default AffiliationGroupDetails;